import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Card, CardContent, CardHeader, Chip, CircularProgress, IconButton, Stack, TextField, Tooltip, Typography,
} from '@mui/material';
import { AutoAwesome as AiIcon, Send as SendIcon, DeleteOutline as ClearIcon } from '@mui/icons-material';
import { apiService } from '../../services/apiService';

const SUGGESTIONS = {
  detection: ['Summarise what happened and why it matters', 'Which indicators look malicious and why?', 'What should I check next on the affected host?', 'Is this likely a false positive?'],
  case: ['Give me a timeline of this incident', 'What containment steps are still open?', 'Which hosts and users are affected?', 'Draft an update for management'],
};

/** Minimal markdown: paragraphs, bullets, bold, inline code. */
export function renderMarkdown(text) {
  const lines = String(text || '').split('\n');
  const out = [];
  let list = [];
  const flush = () => { if (list.length) { out.push(<ul key={`l${out.length}`} style={{ margin: '4px 0 8px 20px', padding: 0 }}>{list}</ul>); list = []; } };
  const inline = (s) => s.split(/(\*\*[^*]+\*\*|`[^`]+`)/g).map((part, i) => {
    if (/^\*\*[^*]+\*\*$/.test(part)) return <strong key={i}>{part.slice(2, -2)}</strong>;
    if (/^`[^`]+`$/.test(part)) return <code key={i} style={{ fontSize: '0.9em' }}>{part.slice(1, -1)}</code>;
    return part;
  });
  lines.forEach((line, i) => {
    const m = line.match(/^\s*[-*•]\s+(.*)/);
    if (m) { list.push(<li key={i}>{inline(m[1])}</li>); return; }
    flush();
    const h = line.match(/^#{1,6}\s+(.*)/);
    if (h) { out.push(<Typography key={i} variant="subtitle2" sx={{ mt: 1 }}>{inline(h[1])}</Typography>); return; }
    if (line.trim()) out.push(<Typography key={i} variant="body2" sx={{ mb: 0.5 }}>{inline(line)}</Typography>);
  });
  flush();
  return out;
}

const CREDITS_MESSAGE = 'Google AI prepaid credits are depleted — a platform administrator needs to top up billing before the assistant can answer.';

/** True when the AI is unavailable specifically because prepaid credits ran out. */
function isCreditsIssue(aiCode, aiError) {
  if (aiCode === 'AI_CREDITS') return true;
  return /credit/i.test(String(aiError || ''));
}

/**
 * Grounded "ask about this record" assistant. Props: targetType ('detection'|'case'), targetId, aiAvailable, aiError, aiCode.
 */
export default function AskAiPanel({ targetType, targetId, aiAvailable = true, aiError = null, aiCode = null, compact = false }) {
  const [history, setHistory] = useState([]);
  const [question, setQuestion] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const bottomRef = useRef(null);

  useEffect(() => { setHistory([]); setError(null); }, [targetType, targetId]);
  useEffect(() => { if (bottomRef.current) bottomRef.current.scrollIntoView({ block: 'nearest' }); }, [history]);

  const ask = async (q) => {
    const text = (q || question).trim();
    if (!text || loading) return;
    setQuestion('');
    setError(null);
    const next = [...history, { role: 'user', content: text }];
    setHistory(next);
    setLoading(true);
    try {
      const res = await apiService.ai.ask(targetType, targetId, text, history);
      setHistory([...next, { role: 'assistant', content: res.data.answer }]);
    } catch (e) {
      setError(e.response?.data?.message || 'The assistant could not answer right now.');
      setHistory(history);
      setQuestion(text);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card sx={{ mb: 3 }} data-testid="ask-ai-panel">
      <CardHeader
        avatar={<AiIcon color="primary" />}
        title="Ask AI about this record"
        subheader={aiAvailable ? 'Answers are grounded in the fields, timeline and enrichment of this record only.' : (aiError || 'AI is not configured')}
        action={history.length > 0 && (
          <Tooltip title="Clear conversation"><IconButton size="small" onClick={() => setHistory([])}><ClearIcon fontSize="small" /></IconButton></Tooltip>
        )}
      />
      <CardContent sx={{ pt: 0 }}>
        {!aiAvailable && (
          <Alert severity="info" sx={{ mb: 1 }}>
            {isCreditsIssue(aiCode, aiError) ? CREDITS_MESSAGE : (aiError || 'Set a valid Google AI API key to enable the assistant.')}
          </Alert>
        )}
        {history.length === 0 && aiAvailable && (
          <Stack direction="row" spacing={1} flexWrap="wrap" useFlexGap sx={{ mb: 1 }}>
            {(SUGGESTIONS[targetType] || []).map((s) => <Chip key={s} label={s} size="small" variant="outlined" onClick={() => ask(s)} disabled={loading} />)}
          </Stack>
        )}
        {history.length > 0 && (
          <Box sx={{ maxHeight: compact ? 260 : 420, overflowY: 'auto', mb: 1, pr: 1 }}>
            {history.map((m, i) => (
              <Box key={i} sx={{ mb: 1.5, pl: 1.5, borderLeft: 3, borderColor: m.role === 'user' ? 'primary.main' : 'divider' }}>
                <Typography variant="caption" color="text.secondary">{m.role === 'user' ? 'You' : 'Excubia AI'}</Typography>
                {m.role === 'user' ? <Typography variant="body2">{m.content}</Typography> : <Box>{renderMarkdown(m.content)}</Box>}
              </Box>
            ))}
            {loading && <Stack direction="row" spacing={1} alignItems="center"><CircularProgress size={16} /><Typography variant="caption">Thinking…</Typography></Stack>}
            <div ref={bottomRef} />
          </Box>
        )}
        {error && <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>{error}</Alert>}
        <Stack direction="row" spacing={1} alignItems="flex-end">
          <TextField
            fullWidth size="small" multiline maxRows={4} placeholder={aiAvailable ? 'Ask a question about this record…' : 'AI unavailable'}
            value={question} onChange={(e) => setQuestion(e.target.value)} disabled={!aiAvailable || loading}
            onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); ask(); } }}
            inputProps={{ 'aria-label': 'Ask AI question' }}
          />
          <Button variant="contained" onClick={() => ask()} disabled={!aiAvailable || loading || !question.trim()} startIcon={<SendIcon />}>Ask</Button>
        </Stack>
      </CardContent>
    </Card>
  );
}
